import React from 'react';

import { generateClassName } from '@klv/shared/utils/functions';
import { useTranslation } from '@klv/hooks/useTranslation';
import Icon from '../../atoms/icon';

import * as styles from './styles/passwordStrength.module.scss';

import type { IPasswordStrengthProps } from './types';

const PasswordStrength = ({ 
  value, 
  isFocused, 
  className, 
  ...props 
}: IPasswordStrengthProps) => {
  const { t } = useTranslation();
  
  if (!isFocused) return null;

  const length = value?.length || 0;
  let characterRange = 0;

  if (value) {
    if (/[a-z]/.test(value)) characterRange += 26;
    if (/[A-Z]/.test(value)) characterRange += 26;
    if (/[0-9]/.test(value)) characterRange += 10;
    if (/[^a-zA-Z0-9]/.test(value)) characterRange += 32;
  }

  const entropy = characterRange > 0 ? length * Math.log2(characterRange) : 0;

  let strengthLevel = 1;
  let strengthLabel = t('molecules_passwordStrength_strength_veryWeak');
  let strengthColor = 'var(--color-poppy-dark)';

  if (entropy >= 70) {
    strengthLevel = 4;
    strengthLabel = t('molecules_passwordStrength_strength_strong');
    strengthColor = 'var(--color-forest-green)';
  } else if (entropy >= 50) {
    strengthLevel = 3;
    strengthLabel = t('molecules_passwordStrength_strength_good');
    strengthColor = 'var(--color-ocean-mid-tint)';
  } else if (entropy >= 30) {
    strengthLevel = 2;
    strengthLabel = t('molecules_passwordStrength_strength_weak');
    strengthColor = 'var(--color-sunflower-yellow)';
  }

  return (
    <div
      className={generateClassName([styles.passwordStrength, className])}
      {...props}
    >
      <div className={styles.strengthBars}>
        {[1, 2, 3, 4].map((barIndex) => {
          const isActive = barIndex <= strengthLevel;
          const customStyle = isActive ? { '--strength-color': strengthColor } as React.CSSProperties : undefined;

          return (
            <div
              key={barIndex}
              className={generateClassName([
                styles.strengthBar,
                isActive && styles.active,
              ])}
              style={customStyle}
            />
          );
        })}
      </div>
      <div className={styles.strengthLabel}>{strengthLabel}</div>
      <div className={styles.requirements}>
        <div className={generateClassName([
          styles.requirement,
          length >= 12 && styles.requirementMet,
        ])}>
          <span className={styles.requirementIcon}>
            <span className={styles.iconWithOutline}>
              <svg className={styles.iconBackground} xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'>
                <circle cx='10' cy='10' r='10' fill={length >= 12 ? 'var(--color-forest-green)' : 'var(--color-charcoal-medium)'} opacity='0.15' />
              </svg>
              <Icon iconName={length >= 12 ? 'CheckBullet' : 'ForbiddenCircle'} 
              fillColor={length >= 12 ? 'var(--color-forest-green)' : 'var(--color-charcoal-medium)'} 
              size={1.5} 
              />
            </span>
          </span>
          <span className={styles.requirementText}>
            {t('molecules_passwordStrength_requirement_minLength')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PasswordStrength;
